/**
 * Stable per-staff chip colours (11E.7).
 *
 * The calendar colour-keys session chips by teacher so an operator can scan
 * a week and see whose day is packed. The mapping must be:
 *
 *  - **stable** across reloads, machines and sessions — a teacher who is
 *    green today must be green tomorrow, or the colour carries no meaning;
 *  - **derived**, not stored — assigning colours in Firestore would add a
 *    per-staff field to maintain and a migration for existing staff.
 *
 * So: hash the teacher id, index into a fixed palette. Same id always
 * yields the same slot.
 *
 * Collisions are expected and acceptable — with 10 palette entries any
 * school past ~5 concurrent teachers will reuse colours. The colour is a
 * scanning aid, never the sole carrier of identity: every chip also shows
 * the subject name, and the session page names the teacher outright.
 */

export interface StaffColor {
  /** Chip background. */
  bg: string;
  /** Chip text — contrast-checked against `bg` (WCAG AA at 11px bold). */
  fg: string;
  /** Left accent bar / colour strip on the session page header. */
  accent: string;
}

/**
 * Ten hue-separated pairs. Backgrounds are deliberately pale so black-ish
 * text stays readable and a chip never fights the grid lines for attention.
 */
const PALETTE: readonly StaffColor[] = [
  { bg: '#e0e7ff', fg: '#3730a3', accent: '#6366f1' }, // indigo
  { bg: '#dcfce7', fg: '#166534', accent: '#22c55e' }, // green
  { bg: '#fef3c7', fg: '#92400e', accent: '#f59e0b' }, // amber
  { bg: '#fce7f3', fg: '#9d174d', accent: '#ec4899' }, // pink
  { bg: '#cffafe', fg: '#155e75', accent: '#06b6d4' }, // cyan
  { bg: '#ede9fe', fg: '#5b21b6', accent: '#8b5cf6' }, // violet
  { bg: '#ffedd5', fg: '#9a3412', accent: '#f97316' }, // orange
  { bg: '#d1fae5', fg: '#065f46', accent: '#10b981' }, // emerald
  { bg: '#e0f2fe', fg: '#075985', accent: '#0ea5e9' }, // sky
  { bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444' }, // red
] as const;

/** Neutral pair for cancelled sessions — colour identity is suppressed. */
export const CANCELLED_COLOR: StaffColor = {
  bg: '#f1f5f9',
  fg: '#94a3b8',
  accent: '#94a3b8',
};

/**
 * FNV-1a (32-bit). Chosen over `String.prototype.charCodeAt` summing
 * because a plain sum collides badly on Firestore's auto-ids, which share
 * long common substrings and differ mostly by character *position* — a
 * sum ignores position entirely and bunched most teachers onto one colour.
 */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in range via Math.imul.
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function staffColor(teacherId: string | null | undefined): StaffColor {
  if (!teacherId) return PALETTE[0];
  return PALETTE[fnv1a(teacherId) % PALETTE.length];
}

export const STAFF_PALETTE_SIZE = PALETTE.length;
